import { useState } from 'react';
import {
  BarChart3, FileText, Shield, Users, Settings, Home,
  MessageSquare, Mail, UserCheck, Building, BookOpen, Upload,
  DollarSign, Link2, Percent, Banknote, PenTool, Activity,
  ChevronDown, Megaphone, Eye, Calendar, Map
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar, SidebarContent, SidebarGroup, SidebarGroupContent,
  SidebarGroupLabel, SidebarMenu, SidebarMenuButton, SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

// ── Sticky dashboards (always visible at top) ──
const dashboardItems = [
  { title: 'App Dashboard', url: '/admin/app', icon: Activity },
  { title: 'Marketing', url: '/admin/marketing', icon: Megaphone },
  { title: 'Weekly Social Review', url: '/admin/marketing/week', icon: Calendar },
  { title: 'Content Review', url: '/admin/content-review', icon: Eye },
  { title: 'Content Calendar', url: '/admin/content-calendar', icon: Calendar },
  { title: 'Social Reporting', url: '/admin/social-reporting', icon: BarChart3 },
];

// ── Collapsible sections ──
interface Section {
  label: string;
  items: { title: string; url: string; icon: any; exact?: boolean }[];
  defaultOpen?: boolean;
}

const sections: Section[] = [
  {
    label: 'Content',
    defaultOpen: false,
    items: [
      { title: 'Blog Posts', url: '/admin/content', icon: PenTool },
      { title: 'Bulk Import', url: '/admin/content/blog/import', icon: Upload },
      { title: 'Site Analytics', url: '/admin/analytics', icon: BarChart3 },
      { title: 'Roadmap', url: '/admin/roadmap', icon: Map },
    ],
  },
  {
    label: 'Forms',
    defaultOpen: false,
    items: [
      { title: 'Contact', url: '/admin/forms/contact', icon: MessageSquare },
      { title: 'Early Adopters', url: '/admin/forms/early-adopters', icon: UserCheck },
      { title: 'Newsletter', url: '/admin/forms/newsletter', icon: Mail },
      { title: 'Professionals', url: '/admin/forms/professionals', icon: Users },
      { title: 'Sponsors', url: '/admin/forms/sponsors', icon: Building },
      { title: 'Stories', url: '/admin/forms/stories', icon: BookOpen },
    ],
  },
  {
    label: 'Referrals',
    defaultOpen: false,
    items: [
      { title: 'Sources', url: '/admin/referrals/sources', icon: Link2 },
      { title: 'Events', url: '/admin/referrals/events', icon: DollarSign },
      { title: 'Rate Tables', url: '/admin/referrals/rates', icon: Percent },
      { title: 'Payouts', url: '/admin/referrals/payouts', icon: Banknote },
    ],
  },
  {
    label: 'System',
    defaultOpen: false,
    items: [
      { title: 'SEO Health', url: '/admin/seo', icon: FileText },
      { title: 'Form Testing', url: '/admin/form-testing', icon: MessageSquare },
      { title: 'Import Professionals', url: '/admin/import', icon: Upload },
      { title: 'Admin Users', url: '/admin/users', icon: Shield },
      { title: 'Settings', url: '/admin/settings', icon: Settings },
    ],
  },
];

export function AdminSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const currentPath = location.pathname;

  // Auto-open the section that contains the current route
  const getInitialOpen = () => {
    const open: Record<string, boolean> = {};
    sections.forEach(s => {
      open[s.label] = s.defaultOpen || s.items.some(i => currentPath.startsWith(i.url));
    });
    return open;
  };

  const [openSections, setOpenSections] = useState<Record<string, boolean>>(getInitialOpen);

  const toggleSection = (label: string) => {
    setOpenSections(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const getNavCls = ({ isActive }: { isActive: boolean }) =>
    isActive ? 'bg-accent text-accent-foreground font-medium' : 'hover:bg-accent/50';

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-64'} collapsible="icon">
      <SidebarContent>
        {/* ── Sticky Dashboards (always visible) ── */}
        <SidebarGroup>
          <SidebarGroupLabel>Dashboards</SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {/* Home link */}
              <SidebarMenuItem>
                <SidebarMenuButton asChild>
                  <NavLink to="/admin" end className={getNavCls}>
                    <Home className="h-4 w-4" />
                    {!collapsed && <span>Home</span>}
                  </NavLink>
                </SidebarMenuButton>
              </SidebarMenuItem>
              {dashboardItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <SidebarMenuButton asChild>
                    <NavLink to={item.url} className={getNavCls}>
                      <item.icon className="h-4 w-4" />
                      {!collapsed && <span>{item.title}</span>}
                    </NavLink>
                  </SidebarMenuButton>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* ── Collapsible sections ── */}
        {sections.map((section) => (
          <SidebarGroup key={section.label}>
            {collapsed ? (
              <SidebarGroupLabel>{section.label}</SidebarGroupLabel>
            ) : (
              <button
                onClick={() => toggleSection(section.label)}
                className="flex items-center justify-between w-full px-3 py-1.5 text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground transition-colors"
              >
                <span>{section.label}</span>
                <ChevronDown
                  className={`h-3.5 w-3.5 transition-transform duration-200 ${
                    openSections[section.label] ? 'rotate-0' : '-rotate-90'
                  }`}
                />
              </button>
            )}
            <div
              className={`overflow-hidden transition-all duration-200 ${
                collapsed || openSections[section.label] ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
              }`}
            >
              <SidebarGroupContent>
                <SidebarMenu>
                  {section.items.map((item) => (
                    <SidebarMenuItem key={item.title}>
                      <SidebarMenuButton asChild>
                        <NavLink to={item.url} end={item.exact} className={getNavCls}>
                          <item.icon className="h-4 w-4" />
                          {!collapsed && <span>{item.title}</span>}
                        </NavLink>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ))}
                </SidebarMenu>
              </SidebarGroupContent>
            </div>
          </SidebarGroup>
        ))}
      </SidebarContent>
    </Sidebar>
  );
}
